// src/components/Dialogs/ExportDialog.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext
} from 'react';
import { Dialog } from '@mui/material';
import {
  Image,
  FileJson,
  Check,
  X,
  Download,
  Palette,
  Grid3x3,
  Sparkles,
  Zap,
  Gauge,
  FileText,
  Film,
  MonitorUp,
  RefreshCw,
  Settings2
} from 'lucide-react';
import { ExportManager, ExportFormat, ExportQuality } from '@features/export';
import { ModelContext } from '@stores/modelStore';
import { useGroupStore } from '@stores/groupStore';
import type { Model } from 'src/types';

interface ExportDialogProps {
  open?: boolean;
  isOpen?: boolean;
  onClose: () => void;
}

const detectCurrentTheme = (): 'dark' | 'light' => {
  if (document.documentElement.classList.contains('dark')) return 'dark';
  if (document.body.classList.contains('dark')) return 'dark';
  const muiTheme = document.querySelector('[data-mui-color-scheme]');
  if (muiTheme?.getAttribute('data-mui-color-scheme') === 'dark') return 'dark';
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches)
    return 'dark';
  return 'light';
};

const getDefaultBackgroundColor = (): string => {
  const theme = detectCurrentTheme();
  return theme === 'dark' ? '#1e293b' : '#ffffff';
};

export const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  isOpen,
  onClose
}) => {
  const dialogOpen = open ?? isOpen ?? false;

  // Get the store instance from context for fresh state access
  const modelStore = useContext(ModelContext);
  if (!modelStore) {
    throw new Error('ModelContext not found');
  }

  // Get groups from group store
  const groups = useGroupStore((state) => state.groups);

  // Export configuration
  const [format, setFormat] = useState<ExportFormat>('png');
  const [quality, setQuality] = useState<ExportQuality>(2);
  const [transparent, setTransparent] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState<string>('');
  const [useAutoTheme, setUseAutoTheme] = useState(true);
  const [includeGrid, setIncludeGrid] = useState(false);
  const [filename, setFilename] = useState('');
  const [gifDurationSeconds, setGifDurationSeconds] = useState(3);
  const [gifFps, setGifFps] = useState(12);
  const [gifLoop, setGifLoop] = useState(true);
  const [currentTheme, setCurrentTheme] = useState<'dark' | 'light'>(
    detectCurrentTheme()
  );

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  const dialogJustOpenedRef = useRef(false);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const exportAbortControllerRef = useRef<AbortController | null>(null);

  const exportManager = React.useMemo(() => new ExportManager(), []);

  // Reset state when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      setExportProgress(0);
      setExportMessage('');
      setExportError(null);
      setExportSuccess(false);
      setIsExporting(false);
    }
  }, [dialogOpen]);

  // Set cooldown when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      dialogJustOpenedRef.current = true;

      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }

      cooldownTimerRef.current = setTimeout(() => {
        dialogJustOpenedRef.current = false;
      }, 500);

      return () => {
        if (cooldownTimerRef.current) {
          clearTimeout(cooldownTimerRef.current);
        }
      };
    }
  }, [dialogOpen]);

  // Theme detection
  useEffect(() => {
    if (dialogOpen) {
      const theme = detectCurrentTheme();
      setCurrentTheme(theme);
      if (!useAutoTheme && !backgroundColor) {
        setBackgroundColor(getDefaultBackgroundColor());
      }
    }
  }, [dialogOpen, useAutoTheme, backgroundColor]);

  // Watch for theme changes
  useEffect(() => {
    if (!dialogOpen) return;

    const observer = new MutationObserver(() => {
      const newTheme = detectCurrentTheme();
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-mui-color-scheme']
    });

    return () => observer.disconnect();
  }, [dialogOpen, currentTheme]);

  const handleFormatChange = useCallback((newFormat: ExportFormat) => {
    setFormat(newFormat);
    if (newFormat === 'jpeg') {
      setTransparent(false);
    }
    if (newFormat === 'gif') {
      setQuality(1);
      setTransparent(false);
    }
    if (newFormat === 'json' || newFormat === 'markdown') {
      setQuality(1);
      setTransparent(false);
      setIncludeGrid(false);
    }
  }, []);

  const handleTransparentToggle = useCallback((checked: boolean) => {
    setTransparent(checked);
    if (checked) {
      setUseAutoTheme(true);
    }
  }, []);

  const handleBackgroundColorChange = useCallback((color: string) => {
    setBackgroundColor(color);
    setUseAutoTheme(false);
  }, []);

  const handleAutoThemeToggle = useCallback(
    (checked: boolean) => {
      setUseAutoTheme(checked);
      if (!checked && !backgroundColor) {
        setBackgroundColor(getDefaultBackgroundColor());
      }
    },
    [backgroundColor]
  );

  const handleExport = useCallback(async () => {
    if (dialogJustOpenedRef.current) {
      return;
    }

    try {
      setIsExporting(true);
      setExportProgress(0);
      setExportMessage('Preparing export...');
      setExportError(null);
      setExportSuccess(false);

      if (format !== 'gif') onClose();

      const abortController = new AbortController();
      exportAbortControllerRef.current = abortController;

      let canvasElement = document.querySelector(
        '[data-testid="isometric-canvas"]'
      ) as HTMLElement;

      if (!canvasElement) {
        canvasElement = document.querySelector(
          '.MuiBox-root[style*="position: relative"]'
        ) as HTMLElement;
      }

      if (!canvasElement) {
        throw new Error('Canvas element not found.');
      }

      const bgColorToPass = transparent
        ? undefined
        : useAutoTheme
          ? undefined
          : backgroundColor;

      const onProgress = (progress: number, message: string) => {
        setExportProgress(progress);
        setExportMessage(message);
      };

      // Set data-exporting attribute to auto-expand labels during export
      canvasElement.setAttribute('data-exporting', 'true');

      // Get the absolute latest model state from store right before export
      const freshModel = modelStore.getState();
      const modelToExport = {
        version: freshModel.version,
        title: freshModel.title,
        description: freshModel.description,
        colors: freshModel.colors,
        icons: freshModel.icons,
        items: freshModel.items,
        views: freshModel.views,
        groups: groups // Include groups for documentation
      };

      const result = await exportManager.exportAndDownload(
        canvasElement,
        modelToExport as Model,
        {
          format,
          quality,
          transparent: format === 'png' ? transparent : undefined,
          backgroundColor: bgColorToPass,
          includeGrid,
          filename: filename || undefined,
          gifDurationSeconds,
          gifFps,
          gifLoop,
          gifMaxColors: 256,
          abortSignal: abortController.signal
        },
        onProgress
      );

      if (result.success) {
        setExportMessage('Export complete!');
        setExportSuccess(true);
      } else {
        throw result.error || new Error('Export failed');
      }
    } catch (error: unknown) {
      console.error('Export error:', error);
      setExportError(error instanceof Error ? error.message : 'Export failed');
      setExportProgress(0);
    } finally {
      // Remove data-exporting attribute
      const canvasElement = document.querySelector(
        '[data-testid="isometric-canvas"]'
      ) as HTMLElement;
      if (canvasElement) {
        canvasElement.removeAttribute('data-exporting');
      }
      setIsExporting(false);
      exportAbortControllerRef.current = null;
    }
  }, [
    exportManager,
    modelStore,
    groups,
    format,
    quality,
    transparent,
    useAutoTheme,
    backgroundColor,
    includeGrid,
    filename,
    gifDurationSeconds,
    gifFps,
    gifLoop,
    onClose
  ]);

  const handleClose = useCallback(() => {
    if (!isExporting) {
      onClose();
    }
  }, [isExporting, onClose]);

  const formatOptions: Array<{
    value: ExportFormat;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    desc: string;
    disabled?: boolean;
  }> = [
    { value: 'png', label: 'PNG', icon: Image, desc: 'Crisp image' },
    { value: 'jpeg', label: 'JPEG', icon: Image, desc: 'Smaller file' },
    { value: 'svg', label: 'SVG', icon: Sparkles, desc: 'Scalable vector' },
    { value: 'gif', label: 'GIF', icon: Film, desc: 'Animation' },
    { value: 'json', label: 'JSON', icon: FileJson, desc: 'Editable data' },
    {
      value: 'markdown',
      label: 'Markdown',
      icon: FileText,
      desc: 'Coming soon',
      disabled: true
    }
  ];

  const qualityOptions = [
    { value: 1, label: '1x', desc: 'Standard', icon: Gauge },
    { value: 2, label: '2x', desc: 'High', icon: Zap },
    { value: 4, label: '4x', desc: 'Ultra', icon: Sparkles }
  ];

  const isDataFormat = format === 'json' || format === 'markdown';
  const selectedFormat = formatOptions.find(
    (option) => option.value === format
  )!;
  const SelectedFormatIcon = selectedFormat.icon;
  const customBackgroundColor = backgroundColor || getDefaultBackgroundColor();

  return (
    <Dialog
      open={dialogOpen}
      onClose={() => {
        if (isExporting || dialogJustOpenedRef.current) {
          return;
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isExporting}
      disableAutoFocus={true}
      disableEnforceFocus={true}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target !== e.currentTarget) {
          e.preventDefault();
        }
      }}
      PaperProps={{
        sx: {
          width: 'min(900px, calc(100vw - 32px))',
          maxHeight: 'min(820px, calc(100vh - 32px))',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 30px 80px rgba(15, 23, 42, 0.28)',
          bgcolor: currentTheme === 'dark' ? '#0f172a' : '#ffffff',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: currentTheme === 'dark' ? '#334155' : '#e2e8f0',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-lg shadow-blue-600/20">
            <MonitorUp className="h-5 w-5" />
          </span>
          <div>
            <h2
              id="export-dialog-title"
              className="text-lg font-semibold text-slate-950 dark:text-white"
            >
              Export diagram
            </h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Choose a format and tune the output.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          disabled={isExporting}
          className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          aria-label="Close export dialog"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid min-h-0 flex-1 md:grid-cols-[230px_minmax(0,1fr)]">
        <aside className="border-b border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-950/40 md:border-b-0 md:border-r">
          <p className="mb-3 px-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            File format
          </p>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-1">
            {formatOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = format === option.value;
              const isDisabled = option.disabled === true;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={isExporting || isDisabled}
                  onClick={() => handleFormatChange(option.value)}
                  aria-pressed={isSelected}
                  title={
                    isDisabled
                      ? `${option.label} export is temporarily unavailable`
                      : undefined
                  }
                  className={`flex min-w-0 items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-50 ${
                    isSelected
                      ? 'border-blue-300 bg-white text-blue-700 shadow-sm dark:border-blue-700 dark:bg-blue-950/50 dark:text-blue-300'
                      : 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-white dark:text-slate-400 dark:hover:border-slate-700 dark:hover:bg-slate-800/70'
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                      isSelected
                        ? 'bg-blue-50 dark:bg-blue-500/10'
                        : 'bg-slate-100 dark:bg-slate-800'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="hidden min-w-0 md:block">
                    <span className="block truncate text-sm font-semibold">
                      {option.label}
                    </span>
                    <span className="block truncate text-xs text-slate-400">
                      {option.desc}
                    </span>
                  </span>
                  <span className="truncate text-xs font-semibold md:hidden">
                    {option.label}
                  </span>
                  {isSelected && (
                    <Check className="ml-auto hidden h-4 w-4 shrink-0 md:block" />
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        <section className="min-h-0 overflow-y-auto px-6 py-5 text-slate-900 dark:text-slate-100">
          <div className="mx-auto max-w-2xl space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <SelectedFormatIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-base font-semibold">
                    {selectedFormat.label} export
                  </h3>
                </div>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {selectedFormat.desc}
                </p>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                .{format === 'markdown' ? 'md' : format}
              </span>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">
                File name
              </span>
              <div className="flex overflow-hidden rounded-xl border border-slate-300 bg-white transition focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 dark:border-slate-600 dark:bg-slate-900">
                <input
                  value={filename}
                  onChange={(event) => setFilename(event.target.value)}
                  placeholder="my-diagram"
                  disabled={isExporting}
                  className="h-11 min-w-0 flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-slate-400 disabled:opacity-50"
                />
                <span className="flex items-center border-l border-slate-200 bg-slate-50 px-3 text-sm text-slate-400 dark:border-slate-700 dark:bg-slate-800/70">
                  .{format === 'markdown' ? 'md' : format}
                </span>
              </div>
            </label>

            {!isDataFormat ? (
              <>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium">Output scale</span>
                    <span className="text-xs text-slate-400">
                      Higher scale produces a larger file
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {qualityOptions
                      .filter((option) => format !== 'gif' || option.value <= 2)
                      .map((option) => {
                        const Icon = option.icon;
                        const isSelected = quality === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            disabled={isExporting}
                            onClick={() =>
                              setQuality(option.value as ExportQuality)
                            }
                            aria-pressed={isSelected}
                            className={`rounded-xl border px-3 py-3 text-left transition ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500 dark:bg-blue-950/40 dark:text-blue-300'
                                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-800/60'
                            }`}
                          >
                            <span className="flex items-center justify-between">
                              <Icon className="h-4 w-4" />
                              {isSelected && <Check className="h-4 w-4" />}
                            </span>
                            <span className="mt-2 block text-sm font-semibold">
                              {option.label}
                            </span>
                            <span className="block text-xs opacity-70">
                              {option.desc}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                </div>

                {format === 'gif' && (
                  <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4 dark:border-violet-900 dark:bg-violet-950/20">
                    <div className="mb-4 flex items-center gap-2">
                      <Film className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                      <span className="text-sm font-semibold">
                        Animation timing
                      </span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label>
                        <span className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
                          Duration
                        </span>
                        <div className="relative">
                          <input
                            type="number"
                            min={0.5}
                            max={10}
                            step={0.5}
                            value={gifDurationSeconds}
                            disabled={isExporting}
                            aria-label="Duration"
                            onChange={(event) =>
                              setGifDurationSeconds(
                                Math.min(
                                  10,
                                  Math.max(
                                    0.5,
                                    Number(event.target.value) || 0.5
                                  )
                                )
                              )
                            }
                            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 pr-12 text-sm outline-none focus:border-violet-500 focus:ring-3 focus:ring-violet-500/10 dark:border-slate-600 dark:bg-slate-900"
                          />
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                            sec
                          </span>
                        </div>
                      </label>
                      <label>
                        <span className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
                          Frame rate
                        </span>
                        <div className="relative">
                          <input
                            type="number"
                            min={5}
                            max={24}
                            step={1}
                            value={gifFps}
                            disabled={isExporting}
                            aria-label="Frame rate"
                            onChange={(event) =>
                              setGifFps(
                                Math.round(
                                  Math.min(
                                    24,
                                    Math.max(5, Number(event.target.value) || 5)
                                  )
                                )
                              )
                            }
                            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 pr-12 text-sm outline-none focus:border-violet-500 focus:ring-3 focus:ring-violet-500/10 dark:border-slate-600 dark:bg-slate-900"
                          />
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                            fps
                          </span>
                        </div>
                      </label>
                    </div>
                    <button
                      type="button"
                      disabled={isExporting}
                      onClick={() => setGifLoop((value) => !value)}
                      className="mt-4 flex w-full items-center justify-between rounded-xl border border-violet-200 bg-white/70 px-3 py-2.5 text-left dark:border-violet-900 dark:bg-slate-900/50"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <RefreshCw className="h-4 w-4" /> Loop animation
                      </span>
                      <span
                        className={`relative h-6 w-11 rounded-full transition ${gifLoop ? 'bg-violet-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                      >
                        <span
                          className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${gifLoop ? 'left-6' : 'left-1'}`}
                        />
                      </span>
                    </button>
                    <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                      {Math.ceil(gifDurationSeconds * gifFps)} frames. Large
                      diagrams are automatically limited to a safe GIF
                      resolution.
                    </p>
                  </div>
                )}

                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <Settings2 className="h-4 w-4 text-slate-400" />
                    <span className="text-sm font-medium">Output options</span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {format === 'png' && (
                      <button
                        type="button"
                        disabled={isExporting}
                        onClick={() => handleTransparentToggle(!transparent)}
                        className={`flex items-center justify-between rounded-xl border px-3 py-3 text-left transition ${
                          transparent
                            ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30'
                            : 'border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <span>
                          <span className="block text-sm font-medium">
                            Transparent background
                          </span>
                          <span className="block text-xs text-slate-400">
                            Useful for presentations
                          </span>
                        </span>
                        <span
                          className={`relative ml-3 h-6 w-11 shrink-0 rounded-full transition ${transparent ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                        >
                          <span
                            className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${transparent ? 'left-6' : 'left-1'}`}
                          />
                        </span>
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={isExporting}
                      onClick={() => setIncludeGrid((value) => !value)}
                      className={`flex items-center justify-between rounded-xl border px-3 py-3 text-left transition ${
                        includeGrid
                          ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30'
                          : 'border-slate-200 dark:border-slate-700'
                      } ${format !== 'png' ? 'sm:col-span-2' : ''}`}
                    >
                      <span className="flex items-start gap-2.5">
                        <Grid3x3 className="mt-0.5 h-4 w-4 text-slate-400" />
                        <span>
                          <span className="block text-sm font-medium">
                            Include editor grid
                          </span>
                          <span className="block text-xs text-slate-400">
                            Keep the canvas grid visible
                          </span>
                        </span>
                      </span>
                      <span
                        className={`relative ml-3 h-6 w-11 shrink-0 rounded-full transition ${includeGrid ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                      >
                        <span
                          className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${includeGrid ? 'left-6' : 'left-1'}`}
                        />
                      </span>
                    </button>
                  </div>
                </div>

                {!transparent && (
                  <div className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400">
                        <Palette className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium">Background</p>
                            <p className="text-xs text-slate-400">
                              Match the editor theme or choose a color
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={isExporting}
                            onClick={() => handleAutoThemeToggle(!useAutoTheme)}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                              useAutoTheme
                                ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                            }`}
                          >
                            Auto · {currentTheme === 'dark' ? 'Dark' : 'Light'}
                          </button>
                        </div>
                        {!useAutoTheme && (
                          <div className="mt-3 flex gap-2">
                            <input
                              type="color"
                              value={customBackgroundColor}
                              onChange={(event) =>
                                handleBackgroundColorChange(event.target.value)
                              }
                              disabled={isExporting}
                              className="h-10 w-12 cursor-pointer rounded-lg border border-slate-300 bg-white p-1 dark:border-slate-600 dark:bg-slate-900"
                              aria-label="Background color picker"
                            />
                            <input
                              value={backgroundColor}
                              onChange={(event) =>
                                handleBackgroundColorChange(event.target.value)
                              }
                              placeholder="#ffffff"
                              disabled={isExporting}
                              className="h-10 min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 font-mono text-sm outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-500/10 dark:border-slate-600 dark:bg-slate-900"
                              aria-label="Background color"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5 dark:border-blue-900 dark:bg-blue-950/20">
                <div className="flex gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm dark:bg-slate-900 dark:text-blue-400">
                    <SelectedFormatIcon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">
                      {format === 'json'
                        ? 'Editable diagram data'
                        : 'Diagram documentation'}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                      {format === 'json'
                        ? 'Exports the complete model so it can be opened and edited again in Flowi.'
                        : 'Creates a readable architecture summary including components, groups, and connections.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isExporting && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/30">
                <div className="mb-2 flex items-center justify-between text-xs font-medium text-blue-700 dark:text-blue-300">
                  <span>{exportMessage || 'Preparing export…'}</span>
                  <span>{exportProgress}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-950">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-all"
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
              </div>
            )}

            {exportSuccess && (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
                <Check className="h-4 w-4" /> Export completed successfully.
              </div>
            )}

            {exportError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                {exportError}
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-slate-200 bg-slate-50/70 px-6 py-4 dark:border-slate-700 dark:bg-slate-950/40">
        <p className="hidden text-xs text-slate-400 sm:block">
          {isDataFormat
            ? 'Exports the current diagram model'
            : `${quality}x ${selectedFormat.label} output`}
        </p>
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (isExporting) exportAbortControllerRef.current?.abort();
              else handleClose();
            }}
            disabled={isExporting && format !== 'gif'}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            {isExporting && format === 'gif' ? 'Cancel export' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting}
            className="inline-flex min-w-36 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-blue-600/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isExporting ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Exporting…
              </>
            ) : exportSuccess ? (
              <>
                <RefreshCw className="h-4 w-4" /> Export again
              </>
            ) : (
              <>
                <Download className="h-4 w-4" /> Export {selectedFormat.label}
              </>
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
